import { z } from "zod";
import i18n from "@/i18n";

const t = (key: string) => i18n.t(key, { ns: "validation" });

export const prescriptionTypeSchema = z.enum(["standard", "controlled", "manipulated", "exam"]);

export const controlRegimeSchema = z.enum([
  "none",
  "antimicrobial",
  "controlled_human",
  "controlled_vet_mapa",
  "manipulated_controlled_mapa",
]);

export type ControlRegime = z.infer<typeof controlRegimeSchema>;

export const buyerSnapshotSchema = z.object({
  name: z.string().optional().or(z.literal("")),
  document: z.string().optional().or(z.literal("")),
  address: z.string().optional().or(z.literal("")),
  city_uf: z.string().optional().or(z.literal("")),
  phone: z.string().optional().or(z.literal("")),
});

export type BuyerSnapshot = z.infer<typeof buyerSnapshotSchema>;

export const prescriptionItemSchema = z.object({
  medication_id: z.string().uuid().optional().or(z.literal("")),
  medication_name: z.string().min(1, t("medicationNameRequired")),
  medication_concentration: z.string().optional().or(z.literal("")),
  pharmaceutical_form: z.string().optional().or(z.literal("")),
  administration_route: z.string().optional().or(z.literal("")),
  presentation: z.string().optional().or(z.literal("")),
  posology_mode: z.enum(["automatic", "free_text"]).default("automatic"),
  free_text_posology: z.string().optional().or(z.literal("")),
  dose_amount: z.string().optional().or(z.literal("")),
  dose_unit: z.string().optional().or(z.literal("")),
  frequency_value: z.string().optional().or(z.literal("")),
  frequency_unit: z.string().optional().or(z.literal("")),
  duration_value: z.string().optional().or(z.literal("")),
  duration_unit: z.string().optional().or(z.literal("")),
});

export const manipulatedComponentSchema = z.object({
  medication_id: z.string().uuid().optional().or(z.literal("")),
  name: z.string().optional().or(z.literal("")),
  concentration: z.string().optional().or(z.literal("")),
  free_text: z.string().optional().or(z.literal("")),
}).superRefine((component, ctx) => {
  if (!component.name?.trim() && !component.free_text?.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["name"], message: t("principleActiveOrMedicationRequired") });
  }
});

export const manipulatedPayloadSchema = z.object({
  components: z.array(manipulatedComponentSchema),
  pharmaceutical_form: z.string().optional().or(z.literal("")),
  treatment_days: z.union([z.string(), z.number()]).optional(),
  total_doses: z.union([z.string(), z.number()]).optional(),
  route: z.string().optional().or(z.literal("")),
  instructions: z.string().optional().or(z.literal("")),
  free_text: z.string().optional().or(z.literal("")),
}).superRefine((payload, ctx) => {
  if (!payload.free_text?.trim() && payload.components.length < 1) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["components"], message: t("atLeastOneComponent") });
  }
});

const basePrescriptionSchema = z.object({
  buyer_mode: z.enum(["blank", "filled"]).default("blank"),
  buyer_snapshot: buyerSnapshotSchema.nullable().optional(),
  pet_id: z.string().uuid(t("selectPatient")),
  clinic_id: z.string().uuid(t("clinicRequired")),
  appointment_id: z.string().uuid().optional().or(z.literal("")),
  medical_record_id: z.string().uuid().optional().or(z.literal("")),
  control_program: z.string().optional().or(z.literal("")),
  control_identifier: z.string().optional().or(z.literal("")),
  control_regime: controlRegimeSchema.default("none"),
  validity_in_days: z.preprocess(
    (value) => {
      if (value === "" || value === null || value === undefined) return undefined;
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed === "") return undefined;
        const parsed = Number(trimmed);
        return Number.isNaN(parsed) ? value : parsed;
      }
      return value;
    },
    z.number().int().positive(t("validityDaysRequired")).max(365).optional(),
  ),
  observations: z.string().optional().or(z.literal("")),
  crmv: z.string().optional().or(z.literal("")),
  mapa_number: z.string().optional().or(z.literal("")),
});

const standardPrescriptionSchema = basePrescriptionSchema.extend({
  type: z.literal("standard"),
  items: z.array(prescriptionItemSchema).min(1, t("atLeastOneMedication")),
  manipulated_payload: z.any().optional(),
});

const controlledPrescriptionSchema = basePrescriptionSchema.extend({
  type: z.literal("controlled"),
  items: z.array(prescriptionItemSchema).min(1, t("atLeastOneMedication")),
  manipulated_payload: z.any().optional(),
});

const manipulatedPrescriptionSchema = basePrescriptionSchema.extend({
  type: z.literal("manipulated"),
  items: z.array(prescriptionItemSchema).optional(),
  manipulated_payload: manipulatedPayloadSchema,
});

export const medicalPrescriptionSchema = z
  .discriminatedUnion("type", [
    standardPrescriptionSchema,
    controlledPrescriptionSchema,
    manipulatedPrescriptionSchema,
  ])
  .superRefine((value, ctx) => {
    if (value.buyer_mode === "filled" && !value.buyer_snapshot?.name?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["buyer_snapshot", "name"],
        message: t("buyerNameRequired"),
      });
    }
  });

export type PrescriptionType = z.infer<typeof prescriptionTypeSchema>;
export type PrescriptionItemInput = z.infer<typeof prescriptionItemSchema>;
export type MedicalPrescriptionInput = z.infer<typeof medicalPrescriptionSchema>;
export type ManipulatedPayloadInput = z.infer<typeof manipulatedPayloadSchema>;
export type ManipulatedComponentInput = z.infer<typeof manipulatedComponentSchema>;

